'use strict';
import React, { useState, useEffect, useMemo } from 'react';
import { useHistory } from 'react-router-dom';
import { useAuth0 } from '@auth0/auth0-react';
import { Box, Grid, Typography, Container, Chip, Paper, Button, TextField, InputAdornment, Collapse } from '@mui/material';
import {
	AutoGraph,
	ScatterPlot,
	BarChart,
	Science,
	Inventory2,
	Download,
	OpenInNew,
	ArrowForward,
	FolderSpecial,
	School,
	Biotech,
	ChevronRight,
	Dashboard,
	LocalHospital,
	Favorite
} from '@mui/icons-material';
import { useProjectsQuery, useProjectCodeQuery } from '@/redux/services/portal/projects.api.js';
import { useProjectsQuery as useDiscoveryQuery } from '@/redux/services/discovery/projects.api';
import { useFetchAllBiovizProjectsQuery } from '@/redux/services/bioviz.api';
import { useBiovizStatus } from '@/lenses/uilenses/hooks/biovizStatus/biovizStatus.hook';
import IBPProjectsModal from './IBPProjectsModal.jsx';
import ProjectsModal from './ProjectsModal.jsx';
import * as config from '@/config/env.config';
import { Brand } from '@/lenses/uilenses/theme/colors';
import { fetchSampleSets } from '@/discovery-panel/api';
import { DiscoveryPanelMap, DISCOVERY_PREFIX, BIOSTRESS, MICROBIOME, LIVERFIBROSIS, KIDNEYFUNCTION } from '@/enums/Enums';
import { getIdTokenFromLocalStorage } from '@/utils/auth0Token';
import './MolecularHome.scss';

const launchSurveyor = () => {
	const surveyorToken = getIdTokenFromLocalStorage();
	const surveyorURL = config.URL_SURVEYOR_SSO + '?token=' + (surveyorToken || '');
	window.open(surveyorURL);
};

// Icon mapping function for productTypes
const getProductTypeIcon = (productType) => {
	const iconMap = {
		[BIOSTRESS]: Science,
		[MICROBIOME]: Biotech,
		[LIVERFIBROSIS]: LocalHospital,
		[KIDNEYFUNCTION]: Favorite
	};
	const IconComponent = iconMap[productType] || Dashboard;
	return <IconComponent />;
};

export const MolecularHome = ({ projects: propsProjects = [], discoveryPanels, onLogEvent }) => {
	const history = useHistory();
	const { isAuthenticated } = useAuth0();
	const { data: allProjects } = useProjectsQuery();
	const projects = (propsProjects && propsProjects.length > 0) ? propsProjects : (allProjects || []);
	const [selectedProjectCode, setSelectedProjectCode] = useState(() => {
		// Clear sessionStorage on login (when authenticated and we haven't cleared it this session)
		if (isAuthenticated) {
			const hasClearedThisSession = sessionStorage.getItem('_selectedProjectCleared');
			if (!hasClearedThisSession) {
				sessionStorage.removeItem('selectedProjectCode');
				sessionStorage.setItem('_selectedProjectCleared', 'true');
				return '';
			}
		}
		return sessionStorage.getItem('selectedProjectCode') || '';
	});
	const [ibpModalOpen, setIbpModalOpen] = useState(false);
	const [projectsModalOpen, setProjectsModalOpen] = useState(false);
	const [isTextFieldSelectionMode, setIsTextFieldSelectionMode] = useState(false);
	const [isMyProjectsSelectionMode, setIsMyProjectsSelectionMode] = useState(false);
	const [sampleSetIdsMap, setSampleSetIdsMap] = useState({});

	// Get projectId from projectCode
	const { data: projectData } = useProjectCodeQuery(selectedProjectCode, { skip: !selectedProjectCode });
	const projectId = projectData?.projectId;

	// Fetch all bioviz projects on mount (only if authenticated)
	const { data: allBiovizProjectsData, isLoading: isLoadingBiovizProjects } = useFetchAllBiovizProjectsQuery(undefined, {
		skip: !isAuthenticated
	});

	// Create a Set of project IDs that have bioviz data for quick lookup
	const biovizProjectIds = useMemo(() => {
		if (!allBiovizProjectsData?.projects || !Array.isArray(allBiovizProjectsData.projects)) {
			return new Set();
		}
		return new Set(allBiovizProjectsData.projects.map(p => p.project_id));
	}, [allBiovizProjectsData]);

	// Filter projects that have bioviz data
	const projectsWithBioviz = useMemo(() => {
		return projects.filter(project => {
			// Check if project.projectId exists in the bioviz projects list
			return project.projectId && biovizProjectIds.has(project.projectId);
		});
	}, [projects, biovizProjectIds]);

	// Check bioviz status for the selected project using the cached list
	const biovizStatus = useMemo(() => {
		if (!projectId) return false;
		return biovizProjectIds.has(projectId);
	}, [projectId, biovizProjectIds]);

	const biovizHref = useMemo(() => {
		if (!biovizStatus || !projectId) return null;
		return config.API_BIOVIZ + `?projectId=${projectId}`;
	}, [biovizStatus, projectId]);

	const biovizLoading = isLoadingBiovizProjects;

	// Get discovery panels for the project
	const { data: discoveryPanelsData } = useDiscoveryQuery(projectId ? [projectId] : [], { skip: !projectId });

	// Extract unique productTypes from discovery panels response
	// Note: API returns productType with DISCOVERY_PREFIX, but we need to strip it for map lookup
	const productTypes = useMemo(() => {
		if (!discoveryPanelsData || !Array.isArray(discoveryPanelsData) || !projectId) {
			return [];
		}
		// Filter discovery panels by projectId and extract unique productTypes
		const projectPanels = discoveryPanelsData.filter(panel => panel.projectId === projectId);
		const uniqueProductTypes = [...new Set(projectPanels.map(panel => {
			// Strip DISCOVERY_PREFIX if present (API returns "DiscoveryPanel_LiverFibrosis", we need "LiverFibrosis")
			const productType = panel.productType || '';
			return productType.startsWith(DISCOVERY_PREFIX) 
				? productType.substring(DISCOVERY_PREFIX.length) 
				: productType;
		}))];
		return uniqueProductTypes.filter(Boolean);
	}, [discoveryPanelsData, projectId]);

	// Fetch sampleSetIds for each productType
	useEffect(() => {
		const fetchSampleSetIds = async () => {
			if (!projectId || productTypes.length === 0) {
				setSampleSetIdsMap({});
				return;
			}

			const newMap = {};
			for (const productType of productTypes) {
				try {
					const sampleSetIds = await fetchSampleSets(projectId, productType);
					newMap[productType] = Array.isArray(sampleSetIds) ? sampleSetIds : [];
				} catch (error) {
					console.error(`Error fetching sampleSetIds for ${productType}:`, error);
					newMap[productType] = [];
				}
			}
			setSampleSetIdsMap(newMap);
		};

		fetchSampleSetIds();
	}, [projectId, productTypes]);

	// Generate discovery panel cards dynamically
	const discoveryPanelCards = useMemo(() => {
		if (productTypes.length === 0) {
			return [];
		}

		return productTypes.map(productType => {
			const sampleSetIds = sampleSetIdsMap[productType] || [];
			const firstSampleSetId = sampleSetIds.length > 0 ? sampleSetIds[0] : null;
			const displayName = DiscoveryPanelMap.ui[productType] || productType;

			return {
				id: `discovery-${productType}`,
				title: `${displayName} Discovery Panel`,
				icon: getProductTypeIcon(productType),
				action: () => {
					if (firstSampleSetId && projectId) {
						history.push(`/discovery-panels/${projectId}/${productType}/${firstSampleSetId}`);
					} else if (!selectedProjectCode) {
						setIsTextFieldSelectionMode(true);
						setProjectsModalOpen(true);
					}
				},
			};
		});
	}, [productTypes, sampleSetIdsMap, projectId, selectedProjectCode, history]);

	// Sync selectedProjectCode to sessionStorage
	useEffect(() => {
		if (selectedProjectCode) {
			sessionStorage.setItem('selectedProjectCode', selectedProjectCode);
		} else {
			sessionStorage.removeItem('selectedProjectCode');
		}
	}, [selectedProjectCode]);

	// Clear sessionStorage on logout (when token is removed)
	useEffect(() => {
		const handleStorageChange = (e) => {
			if (e.key === 'id_token' && e.newValue === null) {
				sessionStorage.removeItem('selectedProjectCode');
				setSelectedProjectCode('');
			}
		};

		window.addEventListener('storage', handleStorageChange);

		if (!isAuthenticated) {
			sessionStorage.removeItem('selectedProjectCode');
			setSelectedProjectCode('');
		}

		return () => {
			window.removeEventListener('storage', handleStorageChange);
		};
	}, []);

	const handleProjectSelected = (project) => {
		if (isMyProjectsSelectionMode && project) {
			setSelectedProjectCode(project.projectCode || '');
			setIsMyProjectsSelectionMode(false);
			setProjectsModalOpen(false);
		} else if (isTextFieldSelectionMode && project) {
			setSelectedProjectCode(project.projectCode || '');
			setIsTextFieldSelectionMode(false);
			setProjectsModalOpen(false);
		}
	};

	const handleProjectsModalClose = () => {
		setIsTextFieldSelectionMode(false);
		setIsMyProjectsSelectionMode(false);
		setProjectsModalOpen(false);
	};

	const applications = [
		{
			id: 'ibp',
			title: 'Bioinformatics Platform',
			description: 'Interactive dashboards & advanced metabolomics analytics.',
			icon: <AutoGraph sx={{ fontSize: 40 }} />,
			action: () => {
				// If no projects have bioviz data, redirect directly to IBP
				if (projectsWithBioviz.length === 0 && !isLoadingBiovizProjects) {
					window.location.href = config.API_BIOVIZ;
				} else {
					setIbpModalOpen(true);
				}
			},
			color: Brand.activatedRed,
			bgGradient: `linear-gradient(135deg, ${Brand.pureWhite} 0%, #FFF5F5 100%)`,
			accentGradient: `linear-gradient(135deg, ${Brand.activatedRed} 0%, ${Brand.insightfulOrange} 100%)`
		},
		{
			id: 'projects',
			title: 'My Projects',
			description: `Access and manage your ${projects.length} active research projects.`,
			icon: <FolderSpecial sx={{ fontSize: 40 }} />,
			action: () => {
				setIsMyProjectsSelectionMode(true);
				setProjectsModalOpen(true);
			},
			badge: projects.length,
			color: Brand.molecularBlue,
			bgGradient: `linear-gradient(135deg, ${Brand.pureWhite} 0%, #F0F7FF 100%)`,
			accentGradient: `linear-gradient(135deg, ${Brand.molecularBlue} 0%, ${Brand.livingPurple} 100%)`
		},
		...(config.ENABLE_KITS_UI ? [{
			id: 'kits',
			title: 'Kits Management',
			description: 'Decentralized platform kits integration.',
			icon: <Inventory2 sx={{ fontSize: 40 }} />,
			action: () => {
				window.location.href = config.API_KITS_UI;
			},
			color: Brand.livingPurple,
			bgGradient: `linear-gradient(135deg, ${Brand.pureWhite} 0%, #F8F5FF 100%)`,
			accentGradient: `linear-gradient(135deg, ${Brand.livingPurple} 0%, ${Brand.molecularBlue} 100%)`
		}] : []),
		{
			id: 'lms',
			title: 'Learning Center',
			description: 'Training resources & certifications.',
			icon: <School sx={{ fontSize: 40 }} />,
			action: () => window.open('https://metabolon.com', '_blank'),
			color: Brand.insightfulOrange,
			bgGradient: `linear-gradient(135deg, ${Brand.pureWhite} 0%, #FFFBF0 100%)`,
			accentGradient: `linear-gradient(135deg, ${Brand.insightfulOrange} 0%, ${Brand.activatedRed} 100%)`
		}
	];

	// Base project tools (without discovery panels)
	// Hide certain tools if discovery panels exist
	// Hide Bioinformatics Platform if project doesn't exist in bioviz
	const baseProjectTools = useMemo(() => {
		const hasDiscoveryPanels = discoveryPanelCards.length > 0;
		const toolsToHide = hasDiscoveryPanels ? ['ibp', 'results', 'pathways', 'stats'] : [];
		
		// Hide Bioinformatics Platform if project doesn't exist in bioviz or if still loading
		// Only show the card when we have a definitive positive result (status === true)
		if (!biovizStatus || biovizLoading) {
			toolsToHide.push('ibp');
		}
		
		const allTools = [
			{
				id: 'ibp',
				title: 'Bioinformatics Platform',
				icon: <AutoGraph />,
				action: () => {
					// Use bioviz href if available, otherwise fallback to base URL
					if (biovizHref) {
						window.location.href = biovizHref;
					} else {
						window.location.href = config.API_BIOVIZ;
					}
				},
			},
			{
				id: 'results',
				title: 'Download Results',
				icon: <Download />,
				action: () => {
					if (selectedProjectCode) {
						history.push(`/home/project/${selectedProjectCode}/results`);
					} else {
						setIsTextFieldSelectionMode(true);
						setProjectsModalOpen(true);
					}
				},
			},
			{
				id: 'pathways',
				title: 'Pathway Explorer',
				icon: <ScatterPlot />,
				action: () => {
					if (selectedProjectCode) {
						history.push(`/home/project/${selectedProjectCode}/pathwayexplorer`);
					} else {
						setIsTextFieldSelectionMode(true);
						setProjectsModalOpen(true);
					}
				},
			},
			{
				id: 'stats',
				title: 'Statistics Table',
				icon: <BarChart />,
				action: () => {
					if (selectedProjectCode) {
						history.push(`/home/project/${selectedProjectCode}/statstable`);
					} else {
						setIsTextFieldSelectionMode(true);
						setProjectsModalOpen(true);
					}
				},
			},
		];

		return toolsToHide.length > 0 
			? allTools.filter(tool => !toolsToHide.includes(tool.id))
			: allTools;
	}, [selectedProjectCode, history, discoveryPanelCards, biovizStatus, biovizHref, biovizLoading]);

	// Merge base project tools with dynamically generated discovery panel cards
	const projectTools = useMemo(() => {
		return [...baseProjectTools, ...discoveryPanelCards];
	}, [baseProjectTools, discoveryPanelCards]);

	return (
		<Box className="MolecularHome" sx={{ 
			background: Brand.metaboliteWhite,
			minHeight: 'calc(100vh - 60px)',
			color: Brand.darkModeBlue,
			position: 'relative',
			overflow: 'hidden'
		}}>
			{/* Background Geometric Accents */}
			<div className="bg-shape shape-1" />
			<div className="bg-shape shape-2" />

			<Container maxWidth="xl" sx={{ position: 'relative', zIndex: 2, pt: { xs: 4, md: 10 }, pb: 10 }}>
				
				{/* Hero Section */}
				<Grid container spacing={6} alignItems="center" sx={{ mb: 10 }}>
					<Grid item xs={12} md={8}>
						<Box sx={{ textAlign: 'left', maxWidth: 900 }}>
							<Chip 
								icon={<Biotech sx={{ color: Brand.molecularBlue }} />}
								label="METABOLOMICS RESEARCH PLATFORM"
								sx={{
									mb: 4,
									background: 'rgba(40, 74, 111, 0.08)',
									color: Brand.molecularBlue,
									border: `1px solid ${Brand.precisionGrey}`,
									fontWeight: 700,
									letterSpacing: 1.2,
									height: 36,
									fontSize: '0.75rem',
									'& .MuiChip-icon': {
										marginLeft: 1.5
									}
								}}
							/>
							<Typography variant="h1" sx={{ 
								fontWeight: 800, 
								fontSize: { xs: '2.75rem', md: '4rem', lg: '4.75rem' },
								letterSpacing: '-0.03em',
								color: Brand.darkModeBlue,
								mb: 3,
								lineHeight: 1.1
							}}>
								Unlocking the Power of <br/>
								<span className="gradient-text">Small Molecules</span>
							</Typography>
							<Typography variant="h5" sx={{ 
								color: Brand.molecularBlue, 
								fontWeight: 400, 
								maxWidth: 700, 
								lineHeight: 1.7,
								opacity: 0.85,
								fontSize: { xs: '1.1rem', md: '1.3rem' },
								mb: 2
							}}>
								Accelerate discovery with our integrated suite of advanced bioinformatics tools, data visualization, and project management.
							</Typography>
						</Box>
					</Grid>
				</Grid>

				{/* Main Applications Grid - 2x2 Layout */}
				<Box sx={{ mb: 10 }}>
					<Typography variant="h6" sx={{ 
						color: Brand.darkModeBlue, 
						mb: 4, 
						fontWeight: 700, 
						fontSize: '1rem',
						display: 'flex',
						alignItems: 'center',
						gap: 1.5,
						textTransform: 'uppercase',
						letterSpacing: 1.5
					}}>
						<span style={{ width: 4, height: 28, background: Brand.activatedRed, borderRadius: 2, display: 'inline-block' }} />
						Core Applications
					</Typography>
					
					<Grid container spacing={3}>
						{applications.map((app) => (
							<Grid item xs={12} sm={6} lg={3} key={app.id}>
								<Paper
									elevation={0}
									onClick={app.action}
									sx={{
										background: app.bgGradient,
										border: `1px solid ${Brand.precisionGrey}`,
										height: '100%',
										minHeight: 320,
										p: 4,
										display: 'flex',
										flexDirection: 'column',
										justifyContent: 'space-between',
										cursor: 'pointer',
										transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
										position: 'relative',
										borderRadius: 4,
										overflow: 'hidden',
										'&:hover': {
											transform: 'translateY(-8px)',
											boxShadow: '0 20px 40px rgba(0, 29, 56, 0.1)',
											borderColor: app.color,
											'& .icon-wrapper': {
												transform: 'scale(1.1) rotate(5deg)',
											}
										}
									}}
								>
									{/* Top Accent Line */}
									<Box sx={{ position: 'absolute', top: 0, left: 0, width: '100%', height: 4, background: app.accentGradient }} />

									<Box>
										{app.badge && (
											<Box sx={{
												position: 'absolute',
												top: 16,
												right: 16,
												background: `${app.color}15`,
												borderRadius: '50%',
												width: 36,
												height: 36,
												display: 'flex',
												alignItems: 'center',
												justifyContent: 'center',
												fontWeight: 700,
												fontSize: '0.9rem',
												color: app.color,
												zIndex: 1
											}}>
												{app.badge}
											</Box>
										)}
										<Box 
											className="icon-wrapper"
											sx={{ 
												display: 'inline-flex', 
												p: 2.5, 
												borderRadius: '16px', 
												background: Brand.pureWhite,
												boxShadow: '0 4px 12px rgba(0,0,0,0.05)',
												color: app.color,
												mb: 3,
												transition: 'transform 0.3s ease'
											}}
										>
											{app.icon}
										</Box>
										<Typography variant="h5" sx={{ 
											fontWeight: 700, 
											fontSize: '1.5rem', 
											mb: 1.5,
											color: Brand.darkModeBlue,
											lineHeight: 1.2
										}}>
											{app.title}
										</Typography>
										<Typography variant="body1" sx={{ color: Brand.molecularBlue, lineHeight: 1.6, opacity: 0.9, fontSize: '0.95rem' }}>
											{app.description}
										</Typography>
									</Box>

									<Box sx={{ display: 'flex', alignItems: 'center', mt: 4, color: app.color, fontWeight: 700, fontSize: '0.9rem', letterSpacing: 0.5 }}>
										OPEN APP <ArrowForward sx={{ ml: 1, fontSize: 18 }} />
									</Box>
								</Paper>
							</Grid>
						))}
					</Grid>
				</Box>

				<Collapse in={!!selectedProjectCode}>
					<Box>
						<Box sx={{ 
							display: 'flex', 
							justifyContent: 'space-between', 
							alignItems: 'center',
							mb: 4
						}}>
							<Typography variant="h6" sx={{ 
								color: Brand.darkModeBlue, 
								fontWeight: 700, 
								fontSize: '1rem',
								display: 'flex',
								alignItems: 'center',
								gap: 1.5,
								textTransform: 'uppercase',
								letterSpacing: 1.5
							}}>
								<span style={{ width: 4, height: 28, background: Brand.molecularBlue, borderRadius: 2, display: 'inline-block' }} />
								Project Tools
							</Typography>
							<Box sx={{ maxWidth: 400, minWidth: 300 }}>
								<TextField
									fullWidth
									placeholder="Click to select a project"
									value={selectedProjectCode}
									onClick={() => {
										setIsTextFieldSelectionMode(true);
										setProjectsModalOpen(true);
									}}
									onFocus={(e) => e.target.blur()}
									InputProps={{
										readOnly: true,
										endAdornment: (
											<InputAdornment position="end">
												<FolderSpecial sx={{ color: Brand.molecularBlue, cursor: 'pointer' }} />
											</InputAdornment>
										),
									}}
									sx={{
										'& .MuiOutlinedInput-root': {
											cursor: 'pointer',
											'&:hover fieldset': {
												borderColor: Brand.molecularBlue,
											},
											'& fieldset': {
												borderColor: Brand.precisionGrey,
											},
										},
									}}
								/>
							</Box>
						</Box>
						<Grid container spacing={2.5}>
						{projectTools.map((tool) => (
							<Grid item xs={12} sm={6} md={3} key={tool.id}>
								<Paper
									elevation={0}
									onClick={tool.action}
									sx={{
										background: Brand.pureWhite,
										border: `1px solid ${Brand.precisionGrey}`,
										p: 3,
										display: 'flex',
										alignItems: 'center',
										cursor: 'pointer',
										borderRadius: 3,
										transition: 'all 0.2s ease',
										minHeight: 80,
										'&:hover': {
											background: Brand.pureWhite,
											boxShadow: '0 8px 24px rgba(0, 29, 56, 0.08)',
											transform: 'translateY(-2px)',
											borderColor: Brand.molecularBlue,
											'& .tool-icon': {
												color: Brand.activatedRed,
												transform: 'scale(1.1)'
											}
										}
									}}
								>
									<Box className="tool-icon" sx={{ color: Brand.molecularBlue, mr: 2.5, display: 'flex', transition: 'all 0.2s' }}>
										{tool.icon}
									</Box>
									<Box sx={{ flexGrow: 1 }}>
										<Typography variant="subtitle1" sx={{ fontWeight: 600, color: Brand.darkModeBlue, lineHeight: 1.2, fontSize: '1rem' }}>
											{tool.title}
										</Typography>
									</Box>
									{tool.external ? 
										<OpenInNew sx={{ fontSize: 18, color: Brand.precisionGrey }} /> : 
										<ChevronRight sx={{ fontSize: 20, color: Brand.precisionGrey }} />
									}
								</Paper>
							</Grid>
						))}
					</Grid>
					</Box>
				</Collapse>

			</Container>

			<IBPProjectsModal
				open={ibpModalOpen}
				onClose={() => setIbpModalOpen(false)}
				projects={projectsWithBioviz}
				isLoading={isLoadingBiovizProjects}
			/>
		<ProjectsModal
			open={projectsModalOpen}
			onClose={handleProjectsModalClose}
			projects={projects}
			onProjectSelect={handleProjectSelected}
			selectMode={isTextFieldSelectionMode || isMyProjectsSelectionMode}
			toolPath={null}
		/>
		</Box>
	);
};

export default MolecularHome;


